import { useState, useCallback, useEffect } from "react";
import { getUserAnnouncements, markAnnouncementRead } from "@/api/announcement";
import type { AnnouncementListItem } from "@/types";

export function useUserAnnouncements(enabled: boolean) {
  const [list, setList] = useState<AnnouncementListItem[]>([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const data = await getUserAnnouncements();
      setList(data);
    } catch {
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const markRead = useCallback(async (id: number) => {
    try {
      await markAnnouncementRead(id);
      setList((prev) =>
        prev.map((item) =>
          item.id === id
            ? { ...item, read_by_user: true, read_count: item.read_count + 1 }
            : item,
        ),
      );
    } catch {
      // ignore
    }
  }, []);

  return { list, loading, refresh, markRead };
}

/**
 * 获取未读站内公告数量。
 * 仅在用户登录时轮询，未登录时返回 0。
 * 每 60 秒自动刷新一次。
 */
export function useUnreadAnnouncements(enabled: boolean) {
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchUnread = useCallback(async () => {
    if (!enabled) {
      setUnreadCount(0);
      return;
    }
    try {
      const data = await getUserAnnouncements();
      setUnreadCount(data.filter((item) => !item.read_by_user).length);
    } catch {
      // 静默处理
    }
  }, [enabled]);

  useEffect(() => {
    fetchUnread();
    if (!enabled) return;
    const timer = setInterval(fetchUnread, 60_000);
    return () => clearInterval(timer);
  }, [fetchUnread, enabled]);

  return { unreadCount, refresh: fetchUnread };
}
